import { useState } from "react";

import { useMountEffect } from "~/hooks/useMountEffect";

import { WatchViewAudio } from "./WatchViewAudio";
import { WatchViewDock } from "./WatchViewDock";
import { WatchViewDockedControls } from "./WatchViewDockedControls";
import { WatchViewDockedNote } from "./WatchViewDockedNote";
import { WatchViewDrawer } from "./WatchViewDrawer";
import { WatchViewHeader } from "./WatchViewHeader";
import { WatchViewHeaderGroup } from "./WatchViewHeaderGroup";
import { WatchViewMain } from "./WatchViewMain";
import { WatchViewMenu } from "./WatchViewMenu";
import { WatchViewNoteEditor } from "./WatchViewNoteEditor";
import { WatchViewSlideLinks } from "./WatchViewSlideLinks";
import { WatchViewSlides } from "./WatchViewSlides";
import { WatchViewStage } from "./WatchViewStage";
import { WatchViewStagePane } from "./WatchViewStagePane";
import { WatchViewVideo } from "./WatchViewVideo";
import { MediaPlayerFullscreenWindowPosition } from "../media/types";

import "./WatchView.css";

import type { CSSProperties, ReactNode } from "react";
import type { WatchViewMediaMode, WatchViewViewMode } from "./WatchViewManager";

export interface WatchViewProps {
  busy?: boolean;
  children?: ReactNode;
  fullscreenSlides?: boolean;
  fullscreenWindowPosition?: MediaPlayerFullscreenWindowPosition;
  mediaMode?: WatchViewMediaMode;
  style?: CSSProperties;
  viewMode?: WatchViewViewMode;
}

export interface WatchViewLayoutEvent {
  DOMRect: DOMRect;
  element: HTMLElement;
}

export const FULLSCREEN_WINDOW_POSITION_STYLE: Map<
  MediaPlayerFullscreenWindowPosition,
  string
> = new Map([
  [MediaPlayerFullscreenWindowPosition.bottomLeft, "bottom-left"],
  [MediaPlayerFullscreenWindowPosition.bottomRight, "bottom-right"],
  [MediaPlayerFullscreenWindowPosition.topLeft, "top-left"],
  [MediaPlayerFullscreenWindowPosition.topRight, "top-right"],
  [MediaPlayerFullscreenWindowPosition.hidden, "hidden"],
]);

export function WatchView({
  busy,
  children,
  fullscreenSlides,
  fullscreenWindowPosition = MediaPlayerFullscreenWindowPosition.bottomLeft,
  mediaMode,
  style,
  viewMode,
}: WatchViewProps) {
  const [isReady, setIsReady] = useState(false);

  useMountEffect(() => {
    // Allow the layout logic (e.g. useBreakpoints) to
    // evaluate before showing the UI. This prevents an
    // initial frame of mis-styled content.
    requestAnimationFrame(() => {
      setIsReady(true);
    });
  });

  return (
    <div
      className="WatchView"
      data-ready={isReady}
      data-media-mode={mediaMode}
      data-view-mode={viewMode}
      data-fullscreen-slides={fullscreenSlides}
      data-fullscreen-window-position={FULLSCREEN_WINDOW_POSITION_STYLE.get(
        fullscreenWindowPosition,
      )}
      aria-busy={busy}
      style={style}
    >
      {children}
    </div>
  );
}

WatchView.Audio = WatchViewAudio;
WatchView.Dock = WatchViewDock;
WatchView.DockedControls = WatchViewDockedControls;
WatchView.DockedNote = WatchViewDockedNote;
WatchView.Drawer = WatchViewDrawer;
WatchView.Header = WatchViewHeader;
WatchView.HeaderGroup = WatchViewHeaderGroup;
WatchView.Main = WatchViewMain;
WatchView.Menu = WatchViewMenu;
WatchView.SlideLinks = WatchViewSlideLinks;
WatchView.Slides = WatchViewSlides;
WatchView.Stage = WatchViewStage;
WatchView.StagePane = WatchViewStagePane;
WatchView.Video = WatchViewVideo;
WatchView.NoteEditor = WatchViewNoteEditor;
